import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import { useNavigate } from 'react-router-dom';

import {
  MILLISECONDS_IN_MINUTE,
  SCHEDULE_SESSION_URL,
  TWO,
  ZERO,
} from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Alert, Button, Snackbar } from '@/ui';
import { track } from '@/utils';

import { GetStartedContext } from '@/components/GetStarted/context';
import {
  useUpdateUserAssessmentMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';
import { captureException } from '@/utils/errorMonitoring';

const ScheduleSession = () => {
  const { user, refetchUser } = useCurrentUser();
  const { nextStep } = useContext(GetStartedContext);
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateUserAssessment] = useUpdateUserAssessmentMutation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const completingRef = useRef(false);

  useEffect(() => {
    track('ob_schedule');
  }, []);

  const ensureAssessmentComplete = useCallback(
    async ({
      refetchAfterUpdate = false,
      surfaceError = false,
    }: {
      refetchAfterUpdate?: boolean;
      surfaceError?: boolean;
    } = {}) => {
      if (!user.id) return;
      if (user.userAssesment?.complete) return;
      if (completingRef.current) return;

      completingRef.current = true;
      try {
        await updateUserAssessment({
          id: user.id,
          userAssesment: { complete: true },
        }).unwrap();

        if (refetchAfterUpdate) {
          await refetchUser();
        }
      } catch (error) {
        captureException(error);
        if (surfaceError) {
          setErrorMessage(
            'We couldn’t mark onboarding as complete. You can continue, but you may be redirected back to Get Started until this saves.',
          );
        }
      } finally {
        // eslint-disable-next-line require-atomic-updates -- ref updates are intentional; we only use this as an in-flight guard
        completingRef.current = false;
      }
    },
    [refetchUser, updateUserAssessment, user.id, user.userAssesment?.complete],
  );

  useEffect(() => {
    if (!user.id) return;
    ensureAssessmentComplete({ refetchAfterUpdate: true }).catch(
      () => undefined,
    );
  }, [ensureAssessmentComplete, user.id]);

  const trackSchedule = async (): Promise<boolean> => {
    try {
      setErrorMessage(null);
      track('ob_scheduledteam_cont');

      const now = new Date();
      now.setHours(ZERO, ZERO, ZERO, ZERO);
      const utc = new Date(
        now.getTime() - now.getTimezoneOffset() * MILLISECONDS_IN_MINUTE,
      );

      await updateUserProfile({
        id: user.id,
        userProfile: {
          scheduleMatchCall: utc.toISOString(),
        },
      }).unwrap();

      await ensureAssessmentComplete({ surfaceError: true });
      await refetchUser();

      return true;
    } catch (e) {
      captureException(e);
      setErrorMessage(
        'We couldn’t save your scheduling info. Please refresh and try again.',
      );
      return false;
    }
  };

  useCalendlyEventListener({
    onEventScheduled: async (e) => {
      e.stopImmediatePropagation();
      e.preventDefault();

      const ok = await trackSchedule();
      if (ok) nextStep(TWO);
    },
  });

  return (
    <div className="flex flex-1 flex-col pt-16 md:pt-0">
      <InlineWidget
        prefill={{
          email: user.email || '',
          name: user.fullName,
        }}
        styles={{ height: '800px' }}
        url={SCHEDULE_SESSION_URL}
      />
      <div className="container mx-auto flex justify-center p-4">
        <Button
          variant="contained"
          onClick={() => navigate('/get-started/get-coach')}
        >
          ← Back to matching preferences
        </Button>
      </div>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={7000}
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage(null)}
      >
        <Alert
          severity="error"
          style={{ width: '100%' }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ScheduleSession;
